import { useState, useMemo } from 'react';
import { GitBranch, FolderKanban, Bot, Users, MessageSquare, Settings, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { cn } from '../../lib/utils';

interface ReconciliationStep {
  id: string;
  action: 'create' | 'update' | 'delete';
  resourceType: string;
  resourceName: string;
  roleCode: string;
  streamId: string;
  targetSystem: string;
  reason: string;
  defaultSelected: boolean;
}

interface StreamEntry {
  streamId: string;
  displayName: string;
  isNew: boolean;
  steps: ReconciliationStep[];
}

interface ReconciliationReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  streams: StreamEntry[];
  onApply: (selectedStepIds: string[]) => void;
  applying?: boolean;
}

const RESOURCE_ICONS: Record<string, React.ReactNode> = {
  github_repo: <GitBranch className="w-4 h-4" />,
  ado_project: <FolderKanban className="w-4 h-4" />,
  bot_registration: <Bot className="w-4 h-4" />,
  teams_team: <Users className="w-4 h-4" />,
  teams_chat: <MessageSquare className="w-4 h-4" />,
  config: <Settings className="w-4 h-4" />,
};

const ACTION_COLORS: Record<string, 'success' | 'warning' | 'destructive'> = {
  create: 'success',
  update: 'warning',
  delete: 'destructive',
};

export function ReconciliationReportDialog({
  open,
  onOpenChange,
  streams,
  onApply,
  applying,
}: ReconciliationReportDialogProps) {
  const allSteps = useMemo(() => streams.flatMap((s) => s.steps), [streams]);
  const [selected, setSelected] = useState<Set<string>>(() =>
    new Set(allSteps.filter((s) => s.defaultSelected).map((s) => s.id)),
  );
  const [expandedStreams, setExpandedStreams] = useState<Set<string>>(() =>
    new Set(streams.map((s) => s.streamId)),
  );

  const counts = useMemo(() => {
    const c = { create: 0, update: 0, delete: 0 };
    for (const step of allSteps) {
      if (selected.has(step.id)) {
        c[step.action]++;
      }
    }
    return c;
  }, [allSteps, selected]);

  function toggleStep(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleStream(streamId: string) {
    setExpandedStreams((prev) => {
      const next = new Set(prev);
      if (next.has(streamId)) next.delete(streamId);
      else next.add(streamId);
      return next;
    });
  }

  function selectAll() {
    setSelected(new Set(allSteps.map((s) => s.id)));
  }

  function selectNone() {
    setSelected(new Set());
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent maxWidth="4xl" className="h-[85vh]">
        <DialogHeader>
          <DialogTitle>Reconciliation Report</DialogTitle>
        </DialogHeader>
        <DialogBody className="overflow-y-auto flex-1 space-y-4">
          <div className="flex items-center gap-3 text-sm">
            <span className="text-green-600 dark:text-green-400 font-medium">
              {counts.create} to create
            </span>
            <span className="text-yellow-600 dark:text-yellow-400 font-medium">
              {counts.update} to update
            </span>
            <span className="text-red-600 dark:text-red-400 font-medium">
              {counts.delete} to delete
            </span>
            <div className="ml-auto flex gap-2">
              <button
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                onClick={selectAll}
              >
                Select all
              </button>
              <button
                className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                onClick={selectNone}
              >
                Select none
              </button>
            </div>
          </div>

          {streams.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
              No changes detected between draft and live configuration.
            </p>
          )}

          {streams.map((stream) => (
            <div
              key={stream.streamId}
              className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            >
              <button
                className="w-full flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                onClick={() => toggleStream(stream.streamId)}
              >
                {expandedStreams.has(stream.streamId) ? (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                )}
                <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
                  {stream.displayName}
                </span>
                {stream.isNew && (
                  <Badge variant="success" className="text-[10px]">New stream</Badge>
                )}
                <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
                  {stream.steps.length} action{stream.steps.length !== 1 ? 's' : ''}
                </span>
              </button>

              {expandedStreams.has(stream.streamId) && (
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {stream.steps.map((step) => (
                    <label
                      key={step.id}
                      className={cn(
                        'flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors',
                        !selected.has(step.id) && 'opacity-50',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(step.id)}
                        onChange={() => toggleStep(step.id)}
                        className="rounded border-gray-300 dark:border-gray-600"
                      />
                      <span className="text-gray-500 dark:text-gray-400">
                        {RESOURCE_ICONS[step.resourceType] || <Settings className="w-4 h-4" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {step.resourceName}
                          </span>
                          <Badge variant={ACTION_COLORS[step.action]} className="text-[10px]">
                            {step.action}
                          </Badge>
                          <Badge variant="outline" className="text-[10px]">
                            {step.targetSystem}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {step.reason} &middot; Role: {step.roleCode}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => onApply(Array.from(selected))}
            disabled={selected.size === 0 || applying}
          >
            {applying ? 'Applying...' : `Apply ${selected.size} Selected`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
